/**
 * «¿Qué me falta?» — cuánto del plano ya está en la hoja de presupuesto y qué queda fuera.
 *
 * Puro y sin tablas: recibe los elementos que SE CONSTRUYEN (la decisión de qué se construye la toma
 * quien llama, con la clase IFC o la clasificación del DWG y las correcciones del usuario) y, por
 * pieza, si ya tiene concepto principal. Devuelve el avance por piezas / área / largo / volumen sumando
 * las medidas reales, y la lista de lo que falta agrupada por elemento (tipo IFC, capa o bloque DWG).
 */

export type MedidaDeAvance = 'piezas' | 'area' | 'longitud' | 'volumen';

/** Una pieza del elemento (o un grupo de piezas iguales si el plano no las trae una a una). */
export interface PiezaParaFalta {
  /** Cuántas piezas representa (1 si viene una a una). */
  n: number;
  area?: number;
  longitud?: number;
  volumen?: number;
  /** ¿Ya tiene concepto principal en la hoja? */
  asignada: boolean;
}

export interface ElementoParaFalta {
  tipo: 'capa' | 'bloque';
  clave: string;
  nombre: string;
  piezas: PiezaParaFalta[];
}

export interface ElementoQueFalta {
  tipo: 'capa' | 'bloque';
  clave: string;
  nombre: string;
  /** Piezas sin concepto. */
  piezasFaltan: number;
  /** Piezas del elemento en total. */
  piezas: number;
  area: number;
  longitud: number;
  volumen: number;
}

export interface AvanceQueMeFalta {
  /** Por medida: lo que suma el plano y lo que ya está en la hoja. Solo las medidas que el plano trae. */
  medidas: { medida: MedidaDeAvance; total: number; hecho: number }[];
  /** Elementos con algo pendiente, de mayor a menor (por piezas pendientes, luego área). */
  faltan: ElementoQueFalta[];
  /** Elementos que se construyen / elementos completos. */
  elementos: number;
  completos: number;
}

const MEDIDAS: MedidaDeAvance[] = ['piezas', 'area', 'longitud', 'volumen'];

function valor(p: PiezaParaFalta, m: MedidaDeAvance): number {
  const v = m === 'piezas' ? p.n : p[m];
  return typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : 0;
}

/**
 * `medidas`: cuáles se informan, en ese orden (IFC: piezas, área, volumen — sumar largos de tipos
 * distintos no mide obra; DWG: piezas, área, largo). Una medida que el plano no trae (total 0) se omite.
 */
export function queMeFalta(
  elementos: readonly ElementoParaFalta[],
  medidas: readonly MedidaDeAvance[] = MEDIDAS,
): AvanceQueMeFalta {
  const total = new Map<MedidaDeAvance, number>();
  const hecho = new Map<MedidaDeAvance, number>();
  const faltan: ElementoQueFalta[] = [];
  let completos = 0;
  for (const el of elementos) {
    let piezasFaltan = 0;
    let piezas = 0;
    const pend = { area: 0, longitud: 0, volumen: 0 };
    for (const p of el.piezas) {
      piezas += valor(p, 'piezas');
      for (const m of medidas) {
        const v = valor(p, m);
        total.set(m, (total.get(m) ?? 0) + v);
        if (p.asignada) hecho.set(m, (hecho.get(m) ?? 0) + v);
      }
      if (!p.asignada) {
        piezasFaltan += valor(p, 'piezas');
        pend.area += valor(p, 'area');
        pend.longitud += valor(p, 'longitud');
        pend.volumen += valor(p, 'volumen');
      }
    }
    if (piezasFaltan > 0 || el.piezas.some((p) => !p.asignada)) {
      faltan.push({ tipo: el.tipo, clave: el.clave, nombre: el.nombre, piezasFaltan, piezas, ...pend });
    } else {
      completos++;
    }
  }
  faltan.sort((a, b) => b.piezasFaltan - a.piezasFaltan || b.area - a.area || b.longitud - a.longitud);
  return {
    medidas: medidas
      .map((medida) => ({ medida, total: total.get(medida) ?? 0, hecho: hecho.get(medida) ?? 0 }))
      .filter((m) => m.total > 0),
    faltan,
    elementos: elementos.length,
    completos,
  };
}

/** Porcentaje presupuestado de una medida (0–100). */
export function porcentajeDeAvance(m: { total: number; hecho: number }): number {
  return m.total > 0 ? Math.min(100, (m.hecho / m.total) * 100) : 0;
}
